/** @jsx jsx */
import { jsx } from '@emotion/core'
import uuid from 'uuid'
import { observer, PropTypes } from 'mobx-react'
import { Fragment } from 'react'
import { GAME_TYPE } from '@game-type/game-market-types'
import { MarketTrack, MatchTrack, TournamentTrack } from '@store/matchupStore'
// icon
import { TimeIco } from '@shared/Icon/MatchupIcon'
import { Win, Lose, normalBet, ResultLose, ResultWin, straightBet } from '@styles/match.style'
import { TxtLimit, TxtLimitPreWrap } from '@styles/base.style'
import { LS_SPORTS_TYPE_NUM, PAGE_TYPE, SPORTS_TYPE_NUM } from '@interface/common'
import {
  addNewLineToTeamName,
  getHomeOrAwayLiveScoreWithPeriodScores,
  getHomeOrAwayScore,
  isSetScoreSport,
  isShowMatchRecordButton,
} from '@utils/utils'
import { HOME_OR_AWAY_SCORE } from '@constants/index'
import { MarketStatusType } from '@game-type/betradar-type'
import { makeVolleyballScore } from '@utils/jsxutils'
import OddsButtonGroup from './OddsButtonGroup'
import _ from 'lodash'
import classNames from 'classnames'
import homeTagMarkImg from 'assets/images/icon/matchup_icon/home-tag-mark.png'
import homeTagMarkGrayImg from 'assets/images/icon/matchup_icon/home-tag-mark-gray.png'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { MatchRecordButton } from '@components/Button/MatchRecordButton'
import { MATCH_GAME_STATUS, MATCH_STATUS } from 'adv-protocol/src/GameCommon'
import TIMER_ICON from '@assets/images/icon/matchup_icon/timer-icon.png'

export enum BLOCK_TYPE {
  WDL = 'wdl',
  HANDICAP = 'handicap',
  UNDEROVER = 'underover',
  LIVE = 'live',
}

export interface OddsBlockProps {
  pageType: PAGE_TYPE
  gameType: GAME_TYPE
  track: TournamentTrack
  match: MatchTrack
  marketTrack?: MarketTrack
  marketType?: string
  blockType: BLOCK_TYPE
  onFieldClickCheck?: () => boolean
  minimumLiveMatchOutcomeRate: number
}

function OddsBlock(props: OddsBlockProps) {
  const { pageType, marketTrack, match, track, blockType, onFieldClickCheck, gameType } = props
  const {
    matchStatusName,
    matchId,
    homeTeamName,
    awayTeamName,
    homeTeamEmblem,
    awayTeamEmblem,
    isOverTimeMatch,
    way,
    statusCode,
    matchStatusCode,
  } = match
  const { sportId, tournamentId } = track

  // ─── 페이지/블록 타입 플래그 ─────────────────────────────────────────────
  /** 경기 결과 페이지 (결과 목록 / 결과 필터) */
  const isResultPage = pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER
  /** 매치업(프리매치) 페이지 */
  const isMatchupPage = pageType === PAGE_TYPE.MATCHUP
  /** 라이브 블록 (blockType 기준) */
  const isLiveBlock = blockType === BLOCK_TYPE.LIVE
  /** 라이브 진행 중인 경기 (statusCode 기준) */
  const isLiveMatch = statusCode === MATCH_GAME_STATUS.Live
  /** 종료된 경기 */
  const isClosedMatch = statusCode === MATCH_GAME_STATUS.Closed

  // ─── 스포츠 종류 플래그 ──────────────────────────────────────────────────
    /** 세트 스코어 종목 (배구, Lsports 배구, 탁구, 테니스) */
  const isSetScoreTarget = isSetScoreSport(sportId)
  /** 무승부가 있는 3-way 종목 (축구, 아이스하키, 핸드볼) */
  const isThreeWaySport = (
    sportId === SPORTS_TYPE_NUM.SOCCER
    || sportId === SPORTS_TYPE_NUM.ICE_HOCKEY
    || sportId === SPORTS_TYPE_NUM.HANDBALL
    || sportId === LS_SPORTS_TYPE_NUM.SOCCER
    || sportId === LS_SPORTS_TYPE_NUM.ICE_HOCKEY
    || sportId === LS_SPORTS_TYPE_NUM.HANDBALL
  )

  // ─── 블록/뷰 키 ──────────────────────────────────────────────────────────
  const blockKey = isLiveBlock ? matchId.toString() : marketTrack?.id
  const viewKey = marketTrack
    ? `${blockType}:${marketTrack.id}`
    : `${blockType}:${uuid.v4()}`

  // ─── 2-way(승패) vs 3-way(승무패) 판단 ──────────────────────────────────
  //   결과 페이지  → way.outcomes 개수로 판단
  //   라이브 블록  → 3-way 종목이면 3-way, 아니면 2-way
  //   프리매치     → 마켓 outcomes 개수로 판단
  let isTwoWay = false
  if (isResultPage) {
    isTwoWay = way ? _.size(way.outcomes) < 3 : false
  } else if (isLiveBlock) {
    isTwoWay = !isThreeWaySport
  } else if (marketTrack) {
    const { markets } = marketTrack
    isTwoWay = markets ? markets[0].outcomes.length < 3 : true
  }

  // ─── 스코어 컴포넌트 (라이브 블록 / 결과 페이지에서 표시) ────────────────
  function makeScoreComponent() {
    // 프리매치 페이지에서는 배당이 없거나 마켓이 비활성이면 스코어를 표시하지 않음
    if (!isResultPage && !isLiveBlock && marketTrack != null) {
      const market = marketTrack.markets?.[0]
      const outcome = market?.outcomes[0]
      const isRateEmpty = outcome?.rate === null || outcome?.rate === 0
      const isMarketInactive = market?.status !== MarketStatusType.Active
      if (isRateEmpty || isMarketInactive) {
        return null
      }
    }

    // ─── 스코어 계산 ───────────────────────────────────────────────────────
    let hScore: string | number
    let aScore: string | number
    let eleHomeScore = []
    let eleAwayScore = []

    // 결과 페이지 + 종료 경기: getScore(최종 스코어) / 그 외: getLiveScore(진행 중 스코어)
    const useFinishedScore = isResultPage && !isLiveMatch
    if (useFinishedScore) {
      hScore = getHomeOrAwayScore(HOME_OR_AWAY_SCORE.HOME, match)
      aScore = getHomeOrAwayScore(HOME_OR_AWAY_SCORE.AWAY, match)
    } else {
      const useLiveScore = isLiveBlock || isLiveMatch
      hScore = getHomeOrAwayLiveScoreWithPeriodScores(HOME_OR_AWAY_SCORE.HOME, match, sportId, useLiveScore)
      aScore = getHomeOrAwayLiveScoreWithPeriodScores(HOME_OR_AWAY_SCORE.AWAY, match, sportId, useLiveScore)

      // 배구·탁구는 세트 스코어를 따로 추출
      if (isSetScoreTarget) {
        const volleyballHomeScore = makeVolleyballScore(hScore)
        const volleyballAwayScore = makeVolleyballScore(aScore)
        hScore = volleyballHomeScore.periodScore
        eleHomeScore = volleyballHomeScore.setScore
        aScore = volleyballAwayScore.periodScore
        eleAwayScore = volleyballAwayScore.setScore
      }
    }

    // ─── 배당 및 승리 결과 ─────────────────────────────────────────────────
    // MobX observable array는 범위 밖 인덱스 접근 시 경고를 발생시키므로,
    // 먼저 outcomes를 추출하고 length 체크 후 인덱스 접근
    const outcomes = way?.outcomes ?? []
    const outcomesLen = outcomes.length

    const homeRate = outcomesLen > 0 ? outcomes[0]?.rate : undefined
    const drawRate = (!isTwoWay && outcomesLen > 1) ? outcomes[1]?.rate : undefined
    const awayRate = isTwoWay
      ? (outcomesLen > 1 ? outcomes[1]?.rate : undefined)
      : (outcomesLen > 2 ? outcomes[2]?.rate : undefined)

    const isHomeWin = outcomesLen > 0 ? (outcomes[0]?.result ?? false) : false
    const isAwayWin = isTwoWay
      ? (outcomesLen > 1 ? (outcomes[1]?.result ?? false) : false)
      : (outcomesLen > 2 ? (outcomes[2]?.result ?? false) : false)
    const isDrawWin = (!isTwoWay && outcomesLen > 1) ? (outcomes[1]?.result ?? false) : false

    // 역배: 이긴 팀의 배당이 상대팀보다 높을 때 (약팀 승리) → 붉은색 표시
    const isHomeWinReverse = isHomeWin && homeRate > awayRate
    const isAwayWinReverse = isAwayWin && awayRate > homeRate

    // ─── 클래스 / 스타일 ───────────────────────────────────────────────────
    /** 3-way(승무패) 종목은 무승부 배당 칸을 표시 */
    const isDrawScore = !isTwoWay

    const liveBoardClass = isResultPage
      ? 'odds-block-list--live-board match-result'
      : 'odds-block-list--live-board'

    const scoreBlockClass = isResultPage
      ? 'odds-block-list--live-board--score result'
      : 'odds-block-list--live-board--score'

    const centerClass = classNames('center', {
      // 'overtime-center': isSoccerSport ? hasPenaltyScore : (isOverTimeMatch || isSetOver),
      'overtime-center': isOverTimeMatch,
    })

    const oddsClass = classNames('', {
      'reverse': isHomeWinReverse || isAwayWinReverse,  // 역배 → 붉은색
      'is-draw-score': isDrawScore,
      'none': _.isNil(homeRate) && _.isNil(drawRate) && _.isNil(awayRate),
    })

    // ─── 결과 페이지 전용 스타일 (일반 페이지와 결과 페이지에서 다른 스타일 사용) ──
    const txtLimitCss = isResultPage ? TxtLimitPreWrap : TxtLimit
    const winStyle = isResultPage ? ResultWin : Win
    const LoseStyle = isResultPage ? ResultLose : Lose
    const homeTeamMark = isResultPage
      ? <img src={homeTagMarkGrayImg} alt="home team" className="home-tag"/>
      : <img src={homeTagMarkImg} alt="home team" className="home-tag"/>
    // 결과 페이지에서는 팀명이 길 경우 줄바꿈 처리
    const displayHomeTeamName = isResultPage ? addNewLineToTeamName(homeTeamName) : homeTeamName
    const displayAwayTeamName = isResultPage ? addNewLineToTeamName(awayTeamName) : awayTeamName

    // ─── 스코어 중앙 배지 표시 조건 ───────────────────────────────────────
    /** 스코어 사이 구분자(:) 표시: 결과 페이지(종료) 또는 매치업 페이지의 라이브 블록 */
    const showScoreSeparator = isMatchupPage && isLiveBlock
    /** 스코어 사이 종료 라벨 표시: 결과 페이지(종료) */
    const showEndLabel = isResultPage && !isLiveMatch
    /** 연장전 배지 표시 */
    const showOvertimeBadge = isResultPage && isOverTimeMatch
    /** 승부차기 배지 표시 (축구, 결과/라이브 페이지) */
    const showPenaltyBadge = (
      [PAGE_TYPE.MATCHUP_RESULT, PAGE_TYPE.MATCHUP_RESULT_FILTER, PAGE_TYPE.LIVE].includes(pageType)
      && matchStatusCode === MATCH_STATUS.Penalties
    )
    /** 라이브 진행 중 상태 텍스트 배지 표시 (결과 페이지에서 아직 진행 중인 경기) */
    const showLiveStatusBadge = isResultPage && isLiveMatch

    return (
      <Fragment>
        <div className={liveBoardClass}>
          {homeTeamMark}

          {/* 홈팀 */}
          <span className="odds-block-list--live-board--team home" css={TxtLimit}>
            <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="home team emblem" className="emblem"/>
            <span css={txtLimitCss} className="name">
              <span>{displayHomeTeamName}</span>
            </span>
          </span>

          {/* 스코어 영역 */}
          <div className={scoreBlockClass}>

            {/* 홈팀 점수 */}
            <span className="left score-num" css={_.toNumber(hScore) > _.toNumber(aScore) ? winStyle : LoseStyle}>
              {isSetScoreTarget && (
                <div className="volleyball-score home">
                  {/* 세트 수만큼 렌더링됩니다 */}
                  {eleHomeScore}
                </div>
              )}
              {hScore}
            </span>

            {/* 스코어 구분자(:) */}
            {showScoreSeparator && <span className={centerClass}>:</span>}

            {/* 종료 라벨 */}
            {showEndLabel && <span className="match-status-label end">종료</span>}

            {/* TODO: 10577 - 세트(배구·탁구·테니스), 승부차기(축구) 배지 */}
            {showOvertimeBadge && (
              <span className="soccer-label"><img src={TIMER_ICON} alt="타이머" className="timer-icon"/>연장</span>
            )}
            {showPenaltyBadge && (
              <span className="soccer-label"><img src={TIMER_ICON} alt="타이머" className="timer-icon"/>승부</span>
            )}
            {showLiveStatusBadge && (
              <span className="match-status-label">{matchStatusName}</span>
            )}

            {/* 원정팀 점수 */}
            <span className="right score-num" css={_.toNumber(aScore) > _.toNumber(hScore) ? winStyle : LoseStyle}>
              {isSetScoreTarget && (
                <div className="volleyball-score away">
                  {eleAwayScore}
                </div>
              )}
              {aScore}
            </span>
          </div>

          {/* 경기 상태 표시 (결과 페이지 제외) */}
          {!isResultPage && (
            <div className="odds-block-list--live-board--status">
              <TimeIco/>
              <span className="name">{matchStatusName}</span>
            </div>
          )}

          {/* 배당 보드 (결과 페이지 + 종료된 경기에서만 표시) */}
          {isResultPage && isClosedMatch && (
            <div className="dividend-board">
              <span className={oddsClass} css={isHomeWin ? straightBet : normalBet}>
                {homeRate}
              </span>
              {isDrawScore && (
                <span className={oddsClass} css={isDrawWin ? straightBet : normalBet}>
                  {drawRate}
                </span>
              )}
              <span className={oddsClass} css={isAwayWin ? straightBet : normalBet}>
                {awayRate}
              </span>
            </div>
          )}

          {/* 원정팀 */}
          <span className="odds-block-list--live-board--team away" css={TxtLimit}>
            <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="away tem emblem" className="emblem"/>
            <span css={txtLimitCss} className="name">
              <span>{displayAwayTeamName}</span>
            </span>
          </span>

          {/* 경기 기록 버튼 */}
          {isShowMatchRecordButton(gameType, pageType, sportId) && (
            <MatchRecordButton
              pageType={pageType}
              matchRecordSimpleMatchInfo={{
                matchId,
                sportId,
                homeScore: match.homeScore,
                awayScore: match.awayScore,
                homeTeamName,
                awayTeamName,
                homeTeamEmblem,
                awayTeamEmblem,
                matchStatusName,
                isLiveMatch,
              }}
            />
          )}
        </div>
      </Fragment>
    )
  }

  return (
    <div key={viewKey} className={`${match.matchId}`}>
      {/* 라이브 블록이거나 결과 페이지(MATCHUP_RESULT)일 때 스코어 표시 */}
      {(isLiveBlock || pageType === PAGE_TYPE.MATCHUP_RESULT) && makeScoreComponent()}

      {/* 배팅 버튼 그룹은 결과 페이지에서 표시하지 않음 */}
      {!isResultPage && (
        <div className="odds-block-list--team-btn">
          <OddsButtonGroup
            pageType={pageType}
            key={`${blockType}:OddsButtonGroup:${blockKey}`}
            oddsBlockType={blockType}
            match={match}
            odds={marketTrack}
            tournamentId={tournamentId}
            track={track}
            onFieldClickCheck={onFieldClickCheck}
            isTwoWay={isTwoWay}
            minimumLiveMatchOutcomeRate={props.minimumLiveMatchOutcomeRate}
          />
        </div>
      )}
    </div>
  )
}

OddsBlock.propTypes = {
  marketTrack: PropTypes.objectOrObservableObject,
}
OddsBlock.defaultProps = {
  marketTrack: null,
}

export default observer(OddsBlock)
